import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppVariants } from '@store/models';

/**
 * Custom TranslateLoader that loads base translations and merges variant-specific overrides
 */
export class VariantTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private prefix: string = './assets/i18n/',
    private suffix: string = '.json',
    private variant?: string
  ) {}

  /**
   * Deep merge two objects, with the override taking precedence
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private deepMerge(base: any, override: any): any {
    const result = { ...base };

    for (const key in override) {
      if (override.hasOwnProperty(key)) {
        if (
          override[key] &&
          typeof override[key] === 'object' &&
          !Array.isArray(override[key])
        ) {
          result[key] = this.deepMerge(result[key] || {}, override[key]);
        } else {
          result[key] = override[key];
        }
      }
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getTranslation(lang: string): Observable<any> {
    const cacheBuster = (Date.now() + '').slice(0, 8); // 8 digits = every minute
    const baseUrl = `${this.prefix}${lang}${this.suffix}?cacheBuster=${cacheBuster}`;

    // If no variant specified, just load base translations
    if (!this.variant) {
      return this.http.get(baseUrl);
    }

    // Determine variant-specific file name
    const variantFileName = this.variant?.toUpperCase() === AppVariants.OCW ? 'nl-ocw' : 'nl-doe';
    const variantUrl = `${this.prefix}${variantFileName}${this.suffix}?cacheBuster=${cacheBuster}`;

    // Load both base and variant-specific translations
    return forkJoin({
      base: this.http.get(baseUrl),
      variant: this.http.get(variantUrl).pipe(
        catchError(() => {
          // If variant file doesn't exist, return empty object
          console.warn(`Variant translation file not found: ${variantUrl}`);
          return of({});
        })
      ),
    }).pipe(
      map(({ base, variant }) => {
        // Merge base with variant overrides
        return this.deepMerge(base, variant);
      })
    );
  }
}
